import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  CircularProgress,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
  useDisclosure,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaCalendarDay,
  FaRegClock,
  FaBoxesPacking,
  FaTag,
  FaClock,
  FaCheck,
  FaXmark,
} from 'react-icons/fa6';
import {
  useCategoryQuery,
  useFetchMyPackages,
} from '../../../hook/useQueryData';
import {
  useAcceptPackageOffer,
  useCancelPackage,
  useDeclinePackageOffer,
  useToggleAutoRenew,
} from '../../../hook/useMutationData';
import { getServiceDataById } from '../../../service/serviceService';
import CustomButton from '../../components/CustomButton';
import CommonConfirmModal from '../../components/common/ui/CommonConfirmModal';
import AcceptOfferCheckoutModal from '../../components/common/ui/AcceptOfferCheckoutModal';
import { formatCurrency } from '../../../utils/currency';
import { CustomerPackage, PackageOfferStatus } from '../../../utils/type';
import { all_routes } from '../../../core/data/routes/all_routes';
import fallbackImage from '../../../../public/fallback-image.png';
import PageHeader from '../../components/common/ui/PageHeader';

const OFFER_STATUS_LABEL: Record<PackageOfferStatus, string> = {
  PENDING_ACCEPTANCE: 'Awaiting your response',
  ACTIVE: 'Active',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

const OFFER_STATUS_COLOR: Record<
  PackageOfferStatus,
  'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'default'
> = {
  PENDING_ACCEPTANCE: 'warning',
  ACTIVE: 'success',
  DECLINED: 'default',
  EXPIRED: 'default',
  CANCELLED: 'danger',
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Phase 3 — My Packages (customer list).
 * Route: /customer-packages
 */
const MyPackages = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data: packages = [],
    isLoading,
    isError,
    refetch,
  } = useFetchMyPackages();
  const { data: categoriesData } = useCategoryQuery();
  const toggleAutoRenewMutation = useToggleAutoRenew();
  const acceptOfferMutation = useAcceptPackageOffer();
  const declineOfferMutation = useDeclinePackageOffer();
  const cancelPackageMutation = useCancelPackage();

  // Track loading states per package ID
  const [acceptingPackageId, setAcceptingPackageId] = useState<string | null>(
    null
  );
  const [decliningPackageId, setDecliningPackageId] = useState<string | null>(
    null
  );
  const [cancellingPackageId, setCancellingPackageId] = useState<string | null>(
    null
  );

  // Details Modal State
  const {
    isOpen: isDetailsOpen,
    onOpen: onOpenDetails,
    onClose: onCloseDetails,
  } = useDisclosure();
  const [selectedPackage, setSelectedPackage] =
    useState<CustomerPackage | null>(null);

  // Accept-Offer Checkout Modal State
  const {
    isOpen: isCheckoutOpen,
    onOpen: onOpenCheckoutModal,
    onOpenChange: onCheckoutOpenChange,
    onClose: onCloseCheckoutModal,
  } = useDisclosure();
  const [checkoutPackage, setCheckoutPackage] =
    useState<CustomerPackage | null>(null);

  // Section Expansion State
  const [expandedSections, setExpandedSections] = useState<{
    pending: boolean;
    active: boolean;
    finished: boolean;
  }>({
    pending: false,
    active: false,
    finished: false,
  });

  const handleOpenDetails = (pkg: CustomerPackage) => {
    setSelectedPackage(pkg);
    onOpenDetails();
  };

  const toggleSection = (section: 'pending' | 'active' | 'finished') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const serviceIds = useMemo(
    () => [...new Set(packages.map((pkg) => pkg.serviceId).filter(Boolean))],
    [packages]
  );
  const serviceQueries = useQueries({
    queries: serviceIds.map((serviceId) => ({
      queryKey: ['MY_PACKAGES_SERVICE_DETAIL', serviceId],
      queryFn: () => getServiceDataById(serviceId),
      enabled: Boolean(serviceId),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });
  const serviceInfoById = useMemo(() => {
    const map = new Map<string, any>();
    serviceQueries.forEach((query, index) => {
      const serviceId = serviceIds[index];
      if (!serviceId) return;
      const info = (query.data as any)?.serviceInfo ?? (query.data as any);
      if (info) map.set(serviceId, info);
    });
    return map;
  }, [serviceQueries, serviceIds]);
  const categoryNameById = useMemo(
    () =>
      new Map(
        (categoriesData?.categories ?? []).map((category) => [
          category.categoryId,
          category.categoryName,
        ])
      ),
    [categoriesData]
  );

  const getCategoryName = (serviceInfo: any) =>
    serviceInfo?.categoryName ||
    serviceInfo?.category?.categoryName ||
    categoryNameById.get(serviceInfo?.categoryId);

  const isMonthly = (pkg: CustomerPackage) =>
    pkg?.tier?.recurrence === 'monthly';

  // Phase 3.1 — how many additional services this package bundles in total
  // (across all its sessions, regardless of which specific session each one
  // applies to). Drives the small "N add-ons" badge on the list cards; the
  // details modal below breaks this down per add-on, per session.
  const getAddonCount = (pkg: CustomerPackage) =>
    pkg.additionalServices?.length ?? 0;

  const pctRemaining = (pkg: CustomerPackage) => {
    const total = pkg.sessionsTotal || 0;
    if (total <= 0) return 0;
    return Math.round((pkg.sessionsRemaining / total) * 100);
  };

  const isProviderOffer = (pkg: CustomerPackage) =>
    pkg?.initiatedBy === 'provider';

  const isPendingOffer = (pkg: CustomerPackage) =>
    isProviderOffer(pkg) && pkg?.offerStatus === 'PENDING_ACCEPTANCE';

  const isActiveCancellable = (pkg: CustomerPackage) => {
    const isExpired = pkg.expiresAt && new Date(pkg.expiresAt) < new Date();
    const isFullyConsumed = pkg.sessionsRemaining <= 0;
    return pkg.offerStatus === 'ACTIVE' && !isExpired && !isFullyConsumed;
  };

  const isPending = (pkg: CustomerPackage) => {
    return pkg?.offerStatus === 'PENDING_ACCEPTANCE';
  };

  const isActive = (pkg: CustomerPackage) => {
    const isExpired = pkg.expiresAt && new Date(pkg.expiresAt) < new Date();
    const isFullyConsumed = pkg.sessionsRemaining <= 0;
    const isActiveOrLegacy =
      pkg.offerStatus === 'ACTIVE' || pkg.offerStatus === undefined;
    return isActiveOrLegacy && !isExpired && !isFullyConsumed;
  };

  const isFinished = (pkg: CustomerPackage) => {
    const isExpired = pkg.expiresAt && new Date(pkg.expiresAt) < new Date();
    const isFullyConsumed = pkg.sessionsRemaining <= 0;
    return !isPending(pkg) && (!isActive(pkg) || isExpired || isFullyConsumed);
  };

  const bookSession = (pkg: CustomerPackage) => {
    navigate(
      all_routes.serviceCreditSession.replace(
        ':serviceId',
        pkg.service?.displayId || pkg.service?.serviceId || pkg.serviceId
      )
    );
  };

  const selectedServiceInfo = selectedPackage
    ? serviceInfoById.get(selectedPackage.serviceId)
    : null;

  // Get filtered packages for each section
  const pendingPackages = packages.filter((pkg) => isPending(pkg));
  const activePackages = packages.filter((pkg) => isActive(pkg));
  const finishedPackages = packages.filter((pkg) => isFinished(pkg));

  // Handle accept offer with per-package loading state
  const handleAcceptOffer = async (packageId: string) => {
    setAcceptingPackageId(packageId);
    try {
      await acceptOfferMutation.mutateAsync(packageId);
    } finally {
      setAcceptingPackageId(null);
    }
  };

  // Handle decline offer with per-package loading state
  const handleDeclineOffer = async (packageId: string) => {
    setDecliningPackageId(packageId);
    try {
      await declineOfferMutation.mutateAsync(packageId);
    } finally {
      setDecliningPackageId(null);
    }
  };

  // Handle cancel package with per-package loading state
  const handleCancelPackage = async (packageId: string) => {
    setCancellingPackageId(packageId);
    try {
      await cancelPackageMutation.mutateAsync({ customerPackageId: packageId });
    } finally {
      setCancellingPackageId(null);
    }
  };

  // Open the checkout modal for a given package's "Accept Offer" action.
  const openAcceptCheckout = (pkg: CustomerPackage) => {
    setCheckoutPackage(pkg);
    onOpenCheckoutModal();
  };

  // Confirming the checkout modal actually performs the accept-offer call.
  // paymentMethod is currently informational only — online isn't wired up yet.
  const handleConfirmAcceptCheckout = async () => {
    if (!checkoutPackage) return;
    await handleAcceptOffer(checkoutPackage.customerPackageId);
    onCloseCheckoutModal();
  };

  // Render section with header, cards, and view all button
  const renderSection = (
    title: string,
    icon: React.ReactNode,
    sectionKey: 'pending' | 'active' | 'finished',
    filteredPackages: CustomerPackage[],
    children: React.ReactNode
  ) => {
    const isExpanded = expandedSections[sectionKey];
    const displayCount = isExpanded
      ? filteredPackages.length
      : Math.min(4, filteredPackages.length);
    const hasMoreThanFour = filteredPackages.length > 4;

    return (
      <div>
        <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gradient-to-r from-primary/30 to-transparent">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 text-primary rounded-lg">
              {icon}
            </div>
            <div>
              <h2 className="text-subtitle2 font-bold text-gray-900">
                {title}
              </h2>
              <p className="text-caption text-gray-500 mt-0.5">
                {filteredPackages.length}{' '}
                {filteredPackages.length === 1 ? 'package' : 'packages'}
                {hasMoreThanFour && !isExpanded && ` (showing ${displayCount})`}
              </p>
            </div>
          </div>
          {hasMoreThanFour && (
            <CustomButton
              label={
                isExpanded
                  ? t('packages.view_less', 'View Less')
                  : t('packages.view_all', 'View All')
              }
              color="primary"
              variant="flat"
              size="sm"
              onPress={() => toggleSection(sectionKey)}
            />
          )}
        </div>
        <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-4 gap-4">
          {children}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <PageHeader
            title={t('packages.my_packages', 'My Packages')}
            description={t(
              'packages.my_packages_subtitle',
              'Manage your purchased session packages.'
            )}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner label="Loading your packages…" />
        </div>
      ) : isError ? (
        <Card className="border border-gray-200">
          <CardBody className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-body text-gray-600">
              {t('packages.load_error', 'We could not load your packages.')}
            </p>
            <CustomButton
              label={t('packages.retry', 'Retry')}
              color="primary"
              variant="flat"
              size="sm"
              onPress={() => refetch()}
            />
          </CardBody>
        </Card>
      ) : packages.length === 0 ? (
        <Card className="border border-gray-200">
          <CardBody className="flex flex-col items-center gap-3 py-16 text-center">
            <FaRegClock size={36} className="text-gray-300" />
            <p className="text-body font-medium text-gray-700">
              {t('packages.empty_title', 'No packages yet')}
            </p>
            <p className="text-caption text-gray-500 max-w-sm">
              {t(
                'packages.empty_hint',
                'When you buy a session package, it will appear here.'
              )}
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-10">
          {/* Pending Offers — awaiting customer acceptance */}
          {pendingPackages.length > 0 &&
            renderSection(
              t('packages.pending_section', 'Pending Offers'),
              <FaClock size={20} />,
              'pending',
              pendingPackages,
              pendingPackages
                .slice(0, expandedSections.pending ? undefined : 4)
                .map((pkg) => {
                  const serviceInfo = serviceInfoById.get(pkg.serviceId);
                  const isAccepting =
                    acceptingPackageId === pkg.customerPackageId;
                  const isDeclining =
                    decliningPackageId === pkg.customerPackageId;
                  const addonCount = getAddonCount(pkg);

                  return (
                    <Card
                      key={pkg.customerPackageId}
                      shadow="sm"
                      className="border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="relative w-full h-[200px] bg-gray-200 overflow-hidden shrink-0">
                        <Image
                          src={
                            serviceInfo?.gallery?.[0]?.serviceImages?.[0] ||
                            fallbackImage
                          }
                          alt={serviceInfo?.serviceTitle || 'Service'}
                          className="w-full h-full object-cover"
                          removeWrapper
                          shadow="none"
                          radius="none"
                        />
                      </div>
                      <CardHeader className="border-b border-gray-100 px-3 py-2 flex justify-between items-start gap-2 min-h-auto">
                        <div className="min-w-0 flex-1">
                          <p className="text-body-bold text-gray-800 truncate">
                            {serviceInfo?.serviceTitle || pkg.serviceId}
                          </p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {getCategoryName(serviceInfo) && (
                              <p className="text-caption text-gray-500 truncate">
                                {getCategoryName(serviceInfo)}
                              </p>
                            )}
                            {addonCount > 0 && (
                              <Chip
                                size="sm"
                                radius="full"
                                color="secondary"
                                variant="flat"
                                className="shrink-0 text-xs"
                              >
                                +{addonCount}{' '}
                                {addonCount === 1 ? 'add-on' : 'add-ons'}
                              </Chip>
                            )}
                          </div>
                        </div>
                        <Chip
                          size="sm"
                          radius="full"
                          color={OFFER_STATUS_COLOR['PENDING_ACCEPTANCE']}
                          variant="flat"
                          className="whitespace-nowrap shrink-0"
                        >
                          {OFFER_STATUS_LABEL['PENDING_ACCEPTANCE']}
                        </Chip>
                      </CardHeader>
                      <CardBody className="px-3 py-2 flex flex-col gap-2">
                        <div className="flex items-center justify-between text-caption text-gray-500">
                          <span>{pkg.sessionsTotal} appointments </span>
                          <span className="flex items-center gap-1">
                            <FaCalendarDay size={11} />
                            {formatDate(pkg.expiresAt)}
                          </span>
                        </div>
                        <div className="text-title text-gray-900 font-semibold">
                          {formatCurrency(
                            pkg.totalPrice || pkg.total || pkg.tier?.price || 0
                          )}
                        </div>
                      </CardBody>
                      <CardFooter className="px-3 py-2 flex flex-col gap-1.5 border-t border-gray-100">
                        {/* View Offer details button */}
                        <CustomButton
                          label={t('packages.view_offer', 'View Offer')}
                          color="secondary"
                          variant="flat"
                          size="sm"
                          fullWidth
                          onPress={() => handleOpenDetails(pkg)}
                          className="bg-primary/10 text-primary"
                        />

                        {/* Accept — opens the checkout modal instead of the plain confirm */}
                        <CustomButton
                          label={t('packages.accept', 'Accept Offer')}
                          color="primary"
                          variant="solid"
                          size="sm"
                          fullWidth
                          isLoading={isAccepting}
                          onPress={() => openAcceptCheckout(pkg)}
                        />

                        {/* Decline offer */}
                        <CommonConfirmModal
                          buttonLabel={t('packages.decline', 'Decline Offer')}
                          buttonColor="danger"
                          buttonVariant="bordered"
                          title={t(
                            'packages.confirm_decline_offer_title',
                            'Decline this package offer?'
                          )}
                          description={t(
                            'packages.confirm_decline_offer_desc',
                            'Declining removes this offer. You can ask the provider for a new one if you change your mind.'
                          )}
                          confirmBtnText={t(
                            'packages.decline_offer',
                            'Decline Offer'
                          )}
                          confirmBtnColor="danger"
                          cancelBtnText={t('packages.cancel', 'Cancel')}
                          isLoading={isDeclining}
                          onConfirm={() =>
                            handleDeclineOffer(pkg.customerPackageId)
                          }
                          triggerClassName="w-full"
                        />
                      </CardFooter>
                    </Card>
                  );
                })
            )}

          {/* Active Packages */}
          {activePackages.length > 0 &&
            renderSection(
              t('packages.active_section', 'Active Packages'),
              <FaCheck size={20} />,
              'active',
              activePackages,
              activePackages
                .slice(0, expandedSections.active ? undefined : 4)
                .map((pkg) => {
                  const monthly = isMonthly(pkg);
                  const providerOffer = isProviderOffer(pkg);
                  const pendingOffer = isPendingOffer(pkg);
                  const offerStatus = pkg.offerStatus;
                  const canBookSession =
                    !providerOffer || offerStatus === 'ACTIVE';
                  const serviceInfo = serviceInfoById.get(pkg.serviceId);
                  const isAccepting =
                    acceptingPackageId === pkg.customerPackageId;
                  const isDeclining =
                    decliningPackageId === pkg.customerPackageId;
                  const isCancelling =
                    cancellingPackageId === pkg.customerPackageId;
                  const addonCount = getAddonCount(pkg);

                  return (
                    <Card
                      key={pkg.customerPackageId}
                      shadow="sm"
                      className="border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="relative w-full h-[200px] bg-gray-200 overflow-hidden shrink-0">
                        <Image
                          src={
                            serviceInfo?.gallery?.[0]?.serviceImages?.[0] ||
                            fallbackImage
                          }
                          alt={
                            serviceInfo?.serviceTitle ||
                            pkg.tier?.name ||
                            'Package'
                          }
                          className="object-cover w-full h-full"
                          shadow="none"
                          removeWrapper
                          isZoomed
                          radius="none"
                        />
                      </div>
                      <CardHeader className="flex flex-col items-start gap-2 pb-2">
                        <div className="w-full min-w-0 flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-subtitle3 font-semibold text-gray-800 line-clamp-2">
                              {serviceInfo?.serviceTitle ||
                                pkg.service?.title ||
                                pkg.tier?.name ||
                                'Package'}
                            </p>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {getCategoryName(serviceInfo) && (
                                <p className="text-caption text-gray-500 line-clamp-1">
                                  {getCategoryName(serviceInfo)}
                                </p>
                              )}
                              {addonCount > 0 && (
                                <Chip
                                  size="sm"
                                  radius="full"
                                  color="secondary"
                                  variant="flat"
                                  className="shrink-0 text-xs"
                                >
                                  +{addonCount}{' '}
                                  {addonCount === 1 ? 'add-on' : 'add-ons'}
                                </Chip>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 justify-end flex-nowrap">
                            {offerStatus && (
                              <Chip
                                size="sm"
                                radius="full"
                                color={OFFER_STATUS_COLOR[offerStatus]}
                                variant="flat"
                                className="shrink-0 whitespace-nowrap"
                              >
                                {t(
                                  `packages.offer_status_${offerStatus}`,
                                  OFFER_STATUS_LABEL[offerStatus]
                                )}
                              </Chip>
                            )}
                            {monthly && (
                              <Chip
                                size="sm"
                                radius="full"
                                color="primary"
                                variant="flat"
                                className="shrink-0 whitespace-nowrap"
                              >
                                Monthly
                              </Chip>
                            )}
                          </div>
                        </div>
                        <p className="text-caption text-gray-400">
                          {pkg.displayId || pkg.customerPackageId}
                        </p>
                      </CardHeader>

                      <CardBody className="py-3 space-y-4">
                        <div className="flex items-center gap-4">
                          <CircularProgress
                            size="lg"
                            aria-label={`${pkg.sessionsRemaining} of ${pkg.sessionsTotal} appointment left`}
                            value={pctRemaining(pkg)}
                            color="primary"
                            showValueLabel
                            classNames={{
                              value: 'text-sm font-bold text-gray-800',
                            }}
                          />
                          <div className="space-y-1">
                            <p className="text-body font-medium text-gray-800">
                              {pkg.sessionsRemaining} / {pkg.sessionsTotal}{' '}
                              {t('packages.sessions', 'appointment left')}
                            </p>
                            {pkg.expiresAt && (
                              <p className="text-caption text-gray-500 flex items-center gap-1.5">
                                <FaCalendarDay
                                  size={12}
                                  className="text-gray-400"
                                />
                                {t('packages.expires', 'Expires')}:{' '}
                                {formatDate(pkg.expiresAt)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-caption text-gray-500">
                            {pendingOffer
                              ? t('packages.offer_price', 'Offer price')
                              : t('packages.paid', 'Paid')}
                            :{' '}
                            {formatCurrency(
                              pkg.totalPrice || pkg.total || pkg.tier?.price
                            )}
                          </span>
                          {monthly && !pendingOffer && (
                            <Switch
                              size="sm"
                              color="primary"
                              isSelected={pkg.autoRenew}
                              isDisabled={toggleAutoRenewMutation.isPending}
                              onValueChange={(val) =>
                                toggleAutoRenewMutation.mutate({
                                  customerPackageId: pkg.customerPackageId,
                                  autoRenew: val,
                                })
                              }
                            >
                              {t('packages.auto_renew_toggle', 'Auto-renew')}
                            </Switch>
                          )}
                        </div>
                      </CardBody>

                      <CardFooter className="pt-2 px-0 py-3">
                        {pendingOffer ? (
                          <div className="flex gap-2 w-full px-3">
                            <div className="flex-1">
                              {/* Accept — opens the checkout modal */}
                              <CustomButton
                                label={t(
                                  'packages.accept_offer',
                                  'Accept Offer'
                                )}
                                color="primary"
                                variant="flat"
                                size="sm"
                                fullWidth
                                isLoading={isAccepting}
                                onPress={() => openAcceptCheckout(pkg)}
                              />
                            </div>
                            <div className="flex-1">
                              <CommonConfirmModal
                                buttonLabel={t(
                                  'packages.decline_offer',
                                  'Decline Offer'
                                )}
                                buttonColor="danger"
                                buttonVariant="flat"
                                title={t(
                                  'packages.confirm_decline_offer_title',
                                  'Decline this package offer?'
                                )}
                                description={t(
                                  'packages.confirm_decline_offer_desc',
                                  'Declining removes this offer. You can ask the provider for a new one if you change your mind.'
                                )}
                                confirmBtnText={t(
                                  'packages.decline_offer',
                                  'Decline Offer'
                                )}
                                confirmBtnColor="danger"
                                cancelBtnText={t('packages.cancel', 'Cancel')}
                                isLoading={isDeclining}
                                onConfirm={() =>
                                  handleDeclineOffer(pkg.customerPackageId)
                                }
                                triggerClassName="w-full"
                              />
                            </div>
                          </div>
                        ) : offerStatus === 'ACTIVE' ? (
                          <div className="flex flex-col gap-2 w-full px-3">
                            <div className="w-full">
                              <CustomButton
                                label={t(
                                  'packages.book_session',
                                  'Book an Appointment'
                                )}
                                color="primary"
                                size="sm"
                                fullWidth
                                isDisabled={pkg.sessionsRemaining <= 0}
                                onPress={() => bookSession(pkg)}
                              />
                            </div>
                            {isActiveCancellable(pkg) && (
                              <div className="w-full">
                                <CommonConfirmModal
                                  buttonLabel={t(
                                    'packages.cancel_package',
                                    'Cancel Package'
                                  )}
                                  buttonColor="danger"
                                  buttonVariant="flat"
                                  title={t(
                                    'packages.confirm_cancel_title',
                                    'Cancel this package?'
                                  )}
                                  description={t(
                                    'packages.confirm_cancel_desc',
                                    `You have ${pkg.sessionsRemaining} session(s) remaining. Cancelling forfeits them — there is no refund and no credit. This cannot be undone.`
                                  )}
                                  confirmBtnText={t(
                                    'packages.cancel_package',
                                    'Cancel Package'
                                  )}
                                  confirmBtnColor="danger"
                                  cancelBtnText={t(
                                    'packages.keep_package',
                                    'Keep Package'
                                  )}
                                  isLoading={isCancelling}
                                  onConfirm={() =>
                                    handleCancelPackage(pkg.customerPackageId)
                                  }
                                  triggerClassName="w-full"
                                />
                              </div>
                            )}
                          </div>
                        ) : canBookSession ? (
                          <div className="w-full px-3">
                            <CustomButton
                              label={t(
                                'packages.book_session',
                                'Book a Session'
                              )}
                              color="primary"
                              size="sm"
                              fullWidth
                              isDisabled={pkg.sessionsRemaining <= 0}
                              onPress={() => bookSession(pkg)}
                            />
                          </div>
                        ) : (
                          <p className="text-center text-caption text-gray-500 w-full px-3">
                            {offerStatus === 'DECLINED'
                              ? t(
                                  'packages.offer_declined_footer',
                                  'You declined this offer.'
                                )
                              : offerStatus === 'CANCELLED'
                                ? t(
                                    'packages.offer_cancelled_footer',
                                    'You cancelled this package.'
                                  )
                                : t(
                                    'packages.offer_expired_footer',
                                    'This offer has expired.'
                                  )}
                          </p>
                        )}
                      </CardFooter>
                    </Card>
                  );
                })
            )}

          {/* Finished Packages — expired, fully consumed, or declined */}
          {finishedPackages.length > 0 &&
            renderSection(
              t('packages.finished_section', 'Finished Packages'),
              <FaXmark size={20} />,
              'finished',
              finishedPackages,
              finishedPackages
                .slice(0, expandedSections.finished ? undefined : 4)
                .map((pkg) => {
                  const monthly = isMonthly(pkg);
                  // const providerOffer = isProviderOffer(pkg);
                  const pendingOffer = isPendingOffer(pkg);
                  const offerStatus = pkg.offerStatus;
                  const serviceInfo = serviceInfoById.get(pkg.serviceId);
                  const isExpired =
                    pkg.expiresAt && new Date(pkg.expiresAt) < new Date();
                  const isFullyConsumed = pkg.sessionsRemaining <= 0;

                  return (
                    <Card
                      key={pkg.customerPackageId}
                      shadow="sm"
                      className="border border-gray-200 overflow-hidden opacity-75 hover:shadow-md transition-shadow"
                    >
                      <div className="relative w-full h-[200px] bg-gray-200 overflow-hidden shrink-0">
                        <Image
                          src={
                            serviceInfo?.gallery?.[0]?.serviceImages?.[0] ||
                            fallbackImage
                          }
                          alt={
                            serviceInfo?.serviceTitle ||
                            pkg.tier?.name ||
                            'Package'
                          }
                          className="object-cover w-full h-full"
                          shadow="none"
                          removeWrapper
                          isZoomed
                          radius="none"
                        />
                      </div>
                      <CardHeader className="flex items-start justify-between gap-3 pb-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-subtitle3 font-semibold text-gray-800">
                            {serviceInfo?.serviceTitle ||
                              pkg.service?.title ||
                              pkg.tier?.name ||
                              'Package'}
                          </p>
                          {getCategoryName(serviceInfo) && (
                            <p className="text-caption text-gray-500 line-clamp-1">
                              {getCategoryName(serviceInfo)}
                            </p>
                          )}
                          <p className="text-caption text-gray-400 mt-0.5">
                            {pkg.displayId || pkg.customerPackageId}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap shrink-0 justify-end">
                          {offerStatus && (
                            <Chip
                              size="sm"
                              radius="full"
                              color={OFFER_STATUS_COLOR[offerStatus]}
                              variant="flat"
                              className="shrink-0 whitespace-nowrap text-xs"
                            >
                              {t(
                                `packages.offer_status_${offerStatus}`,
                                OFFER_STATUS_LABEL[offerStatus]
                              )}
                            </Chip>
                          )}
                          {isExpired && (
                            <Chip
                              size="sm"
                              radius="full"
                              color="default"
                              variant="flat"
                              className="shrink-0 text-xs"
                            >
                              Expired
                            </Chip>
                          )}
                          {isFullyConsumed && !isExpired && (
                            <Chip
                              size="sm"
                              radius="full"
                              color="default"
                              variant="flat"
                              className="shrink-0 text-xs"
                            >
                              Finished
                            </Chip>
                          )}
                        </div>
                      </CardHeader>

                      <CardBody className="py-3 space-y-4">
                        <div className="flex items-center gap-4">
                          <CircularProgress
                            size="lg"
                            aria-label={`${pkg.sessionsRemaining} of ${pkg.sessionsTotal} appointments left`}
                            value={pctRemaining(pkg)}
                            color="primary"
                            showValueLabel
                            classNames={{
                              value: 'text-sm font-bold text-gray-800',
                            }}
                          />
                          <div className="space-y-1">
                            <p className="text-body font-medium text-gray-800">
                              {pkg.sessionsRemaining} / {pkg.sessionsTotal}{' '}
                              {t('packages.sessions', 'appointment left')}
                            </p>
                            {pkg.expiresAt && (
                              <p className="text-caption text-gray-500 flex items-center gap-1.5">
                                <FaCalendarDay
                                  size={12}
                                  className="text-gray-400"
                                />
                                {t('packages.expires', 'Expires')}:{' '}
                                {formatDate(pkg.expiresAt)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-caption text-gray-500">
                            {pendingOffer
                              ? t('packages.offer_price', 'Offer price')
                              : t('packages.paid', 'Paid')}
                            :{' '}
                            {formatCurrency(
                              pkg.totalPrice || pkg.total || pkg.tier?.price
                            )}
                          </span>
                          {monthly && !pendingOffer && (
                            <Switch
                              size="sm"
                              color="primary"
                              isSelected={pkg.autoRenew}
                              isDisabled={toggleAutoRenewMutation.isPending}
                              onValueChange={(val) =>
                                toggleAutoRenewMutation.mutate({
                                  customerPackageId: pkg.customerPackageId,
                                  autoRenew: val,
                                })
                              }
                            >
                              {t('packages.auto_renew_toggle', 'Auto-renew')}
                            </Switch>
                          )}
                        </div>
                      </CardBody>

                      <CardFooter className="pt-2 px-0 py-3">
                        <p className="text-center text-caption text-gray-500 w-full px-3">
                          {isFullyConsumed && !isExpired
                            ? t(
                                'packages.fully_consumed',
                                'All appointments used'
                              )
                            : isExpired
                              ? t(
                                  'packages.package_expired',
                                  'This package has expired'
                                )
                              : t('packages.no_action', 'No actions available')}
                        </p>
                      </CardFooter>
                    </Card>
                  );
                })
            )}
        </div>
      )}

      {/* Complete View Package Offer Details Modal */}
      <Modal
        isOpen={isDetailsOpen}
        onOpenChange={onCloseDetails}
        size="3xl"
        scrollBehavior="inside"
        hideCloseButton={true}
        className="max-h-[90vh]"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 py-3">
                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                  <FaBoxesPacking size={22} />
                </div>
                <div>
                  <h3 className="text-subtitle1 font-bold text-gray-900">
                    {t('packages.offer_details_title', 'Package Offer Details')}
                  </h3>
                  <p className="text-caption text-gray-500">
                    {selectedPackage?.displayId ||
                      selectedPackage?.customerPackageId}
                  </p>
                </div>
              </ModalHeader>

              <ModalBody className="py-4 space-y-4">
                {selectedPackage && (
                  <>
                    {/* Header Summary Box */}
                    <div className="flex flex-col sm:flex-row gap-4 p-3.5 bg-gray-50 rounded-xl border border-gray-100 items-start sm:items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Image
                          src={
                            selectedServiceInfo?.gallery?.[0]
                              ?.serviceImages?.[0] || fallbackImage
                          }
                          alt="Service"
                          className="w-14 h-14 object-cover rounded-lg shrink-0"
                        />
                        <div>
                          <p className="text-body-bold text-gray-800">
                            {selectedServiceInfo?.serviceTitle ||
                              selectedPackage.serviceId}
                          </p>
                        </div>
                      </div>
                      <Chip
                        size="md"
                        color={
                          OFFER_STATUS_COLOR[
                            selectedPackage.offerStatus || 'PENDING_ACCEPTANCE'
                          ]
                        }
                        variant="flat"
                      >
                        {
                          OFFER_STATUS_LABEL[
                            selectedPackage.offerStatus || 'PENDING_ACCEPTANCE'
                          ]
                        }
                      </Chip>
                    </div>

                    {/* Compact Grid Info Section */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                        <span className="text-caption text-gray-500 block mb-0.5">
                          Total Appointments
                        </span>
                        <p className="text-body-bold text-gray-900">
                          {selectedPackage.sessionsTotal}
                        </p>
                      </div>
                      <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                        <span className="text-caption text-gray-500 block mb-0.5">
                          Validity
                        </span>
                        <p className="text-body-bold text-gray-900">
                          {selectedPackage.tier?.validityDays
                            ? `${selectedPackage.tier.validityDays} Days`
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                        <span className="text-caption text-gray-500 block mb-0.5">
                          Recurrence
                        </span>
                        <p className="text-body-bold text-gray-900 capitalize">
                          {selectedPackage.tier?.recurrence || 'Block'}
                        </p>
                      </div>
                      <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100">
                        <span className="text-caption text-gray-500 block mb-0.5">
                          Expires On
                        </span>
                        <p className="text-body-bold text-gray-900">
                          {formatDate(selectedPackage.expiresAt)}
                        </p>
                      </div>
                    </div>

                    {/* Tier Information */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1.5 text-body-bold text-gray-800">
                        <FaTag className="text-primary" size={14} />
                        <span>Package Tier: {selectedPackage.tier?.name}</span>
                      </div>
                    </div>

                    {/* Additional Services — Phase 3.1: each selected add-on
                        shows exactly which session(s) of this package it's
                        bundled into (sessionNumbers, snapshotted at
                        purchase/offer time), and its contribution to the
                        total (price × however many sessions it covers). An
                        addon assigned to only some sessions (e.g. session 2
                        of 2) is visibly distinct from one assigned to every
                        session, instead of a single opaque add-ons total. */}
                    {(selectedPackage.additionalServices?.length ?? 0) > 0 && (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5 text-body-bold text-gray-800">
                          <FaBoxesPacking className="text-primary" size={14} />
                          <span>Additional Services</span>
                        </div>
                        <div className="space-y-1.5">
                          {selectedPackage.additionalServices!.map((addon) => {
                            const sessions = addon.sessionNumbers?.length
                              ? addon.sessionNumbers
                              : [];
                            const sessionCount =
                              sessions.length || 1; /* legacy fallback: no
                              sessionNumbers recorded means it applied to
                              every session at the flat pre-3.1 price, so a
                              single unit is the least-wrong assumption. */
                            return (
                              <div
                                key={addon.id}
                                className="flex items-center justify-between gap-2 p-2.5 bg-gray-50/80 rounded-lg border border-gray-100"
                              >
                                <div className="min-w-0">
                                  <p className="text-body text-gray-800 truncate">
                                    {addon.serviceItem}
                                  </p>
                                  {sessions.length > 0 && (
                                    <p className="text-caption text-gray-500">
                                      Appointment
                                      {sessions.length > 1 ? 's' : ''}{' '}
                                      {sessions.join(', ')}
                                    </p>
                                  )}
                                </div>
                                <p className="text-body-bold text-gray-700 whitespace-nowrap">
                                  {formatCurrency(
                                    (addon.price || 0) * sessionCount
                                  )}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Pricing Summary Breakdown */}
                    <div className="space-y-1.5 pt-1 border-2 border-secondary/10 rounded-lg p-3.5 bg-secondary/10">
                      <div className="flex justify-between text-body text-gray-700">
                        <span>Package Price</span>
                        <span>
                          {formatCurrency(selectedPackage.tier?.price || 0)}
                        </span>
                      </div>
                      {(selectedPackage.addonSubtotal || 0) > 0 && (
                        <div className="flex justify-between text-body text-gray-700">
                          <span>Add-ons Subtotal</span>
                          <span>
                            {formatCurrency(selectedPackage.addonSubtotal || 0)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-subtitle2 font-bold text-gray-900 pt-2 border-t border-gray-100">
                        <span>
                          {isPending(selectedPackage) ? 'Offer Price' : 'Total'}
                        </span>
                        <span className="text-primary">
                          {formatCurrency(
                            selectedPackage.totalPrice ||
                              selectedPackage.total ||
                              (selectedPackage.tier?.price || 0) +
                                (selectedPackage.addonSubtotal || 0)
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </ModalBody>

              <ModalFooter className="border-t border-gray-100 py-3 flex gap-2">
                <CustomButton
                  label={t('packages.close', 'Close')}
                  color="default"
                  variant="flat"
                  size="sm"
                  onPress={onClose}
                />
                {selectedPackage && isPending(selectedPackage) && (
                  <CustomButton
                    label={t('packages.accept_offer', 'Accept Offer')}
                    color="primary"
                    variant="solid"
                    size="sm"
                    isLoading={
                      acceptingPackageId === selectedPackage.customerPackageId
                    }
                    onPress={() => {
                      onClose();
                      openAcceptCheckout(selectedPackage);
                    }}
                  />
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Accept Offer Checkout Modal */}
      <AcceptOfferCheckoutModal
        open={isCheckoutOpen}
        onOpenChange={onCheckoutOpenChange}
        pkg={checkoutPackage}
        isLoading={
          Boolean(checkoutPackage) &&
          acceptingPackageId === checkoutPackage?.customerPackageId
        }
        onConfirm={handleConfirmAcceptCheckout}
      />
    </div>
  );
};

export default MyPackages;
